import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { STATUT_LABELS, STATUT_COLORS, CATEGORIE_LABELS } from "@/lib/prospects";
import { Input } from "@/components/ui/input";
import { Mail, MessageSquare, Clock, XCircle, CalendarClock } from "lucide-react";

const EMAIL_TYPES = { premier_email: "Premier e-mail (J0)", relance_1: "Relance 1", relance_2: "Relance 2", relance_3: "Relance 3", relance_programmee: "Relance automatique (date promise)" };

export const ProspectDetail = ({ prospectId, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [relanceDate, setRelanceDate] = useState("");

  const saveRelanceDate = async (dateVal) => {
    try {
      const { data: res } = await api.put(`/prospects/${prospectId}/relance-date`, { date: dateVal });
      toast.success(res.message);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const load = () => api.get(`/prospects/${prospectId}`).then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { if (prospectId) load(); }, [prospectId]);

  const simulate = async () => {
    if (!reply.trim()) return;
    setAnalyzing(true);
    try {
      const { data: res } = await api.post(`/prospects/${prospectId}/simulate-reply`, { texte: reply });
      toast.success(`Réponse classée : ${CATEGORIE_LABELS[res.analyse.categorie]} — relances arrêtées`);
      setReply("");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAnalyzing(false);
    }
  };

  if (!prospectId) return null;
  const p = data?.prospect;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="prospect-detail-dialog">
        {!p ? <p className="text-sm text-muted-foreground">Chargement…</p> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display flex flex-wrap items-center gap-2">
                {p.raison_sociale}
                <Badge className={STATUT_COLORS[p.statut]}>{STATUT_LABELS[p.statut]}</Badge>
              </DialogTitle>
            </DialogHeader>

            <div className="grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
              <p><span className="text-muted-foreground">Secteur :</span> {p.secteur || "—"}</p>
              <p><span className="text-muted-foreground">Ville :</span> {p.ville || "—"} {p.departement && `(${p.departement})`}</p>
              <p><span className="text-muted-foreground">Contact :</span> {p.contact_prenom} {p.contact_nom || "—"}</p>
              <p><span className="text-muted-foreground">Fonction :</span> {p.contact_fonction || "—"}</p>
              <p><span className="text-muted-foreground">E-mail :</span> {p.contact_email || p.email_generique || "—"}</p>
              <p><span className="text-muted-foreground">Téléphone :</span> {p.telephone || "—"}</p>
              <p><span className="text-muted-foreground">Source :</span> {p.source}</p>
              <p><span className="text-muted-foreground">Site :</span> {p.site_internet || "—"}</p>
            </div>
            {p.besoin_potentiel && <p className="rounded-md bg-amber-50 p-2.5 text-sm"><span className="font-medium">Besoin potentiel :</span> {p.besoin_potentiel}</p>}
            {p.sequence?.stopped_reason && (
              <p className="flex items-center gap-2 rounded-md bg-red-50 p-2.5 text-sm text-red-800"><XCircle size={15} /> Séquence arrêtée : {p.sequence.stopped_reason}</p>
            )}

            {p.reponse_resume && (
              <div className="rounded-md border border-accent/40 bg-accent/5 p-3 text-sm">
                <p className="font-medium">Analyse IA de la dernière réponse — {CATEGORIE_LABELS[p.reponse_categorie]}</p>
                <p className="mt-1">{p.reponse_resume}</p>
                {p.besoin && <p className="mt-1"><span className="text-muted-foreground">Besoin :</span> {p.besoin} {p.urgence && `· urgence ${p.urgence}`}</p>}
                <p className="mt-1 text-muted-foreground">Action recommandée : {p.action_recommandee} · Priorité {p.priorite} · {p.delai_recommande}</p>
              </div>
            )}

            {p.score !== undefined && p.score !== null && (
              <div className="rounded-md border p-3 text-sm" data-testid="prospect-score-section">
                <p className="font-medium">Score commercial : {p.score}/100</p>
                <div className="mt-1.5 space-y-0.5">
                  {(p.score_details || []).map((d, i) => (
                    <div key={i} className="flex justify-between gap-2 text-xs">
                      <span className="text-muted-foreground">{d.critere}</span>
                      <span className={d.points < 0 ? "text-red-600" : "text-green-700"}>{d.points > 0 ? "+" : ""}{d.points}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(p.statut === "a_recontacter" || p.prochaine_relance) && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="relance-programmee-section">
                <p className="flex items-center gap-2 font-medium"><CalendarClock size={15} /> Relance automatique programmée</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {p.prochaine_relance
                    ? `Un e-mail de relance personnalisé sera envoyé automatiquement le ${new Date(p.prochaine_relance).toLocaleDateString("fr-FR")}, avec une tâche de suivi pour le commercial.`
                    : "Aucune relance programmée. Choisissez une date pour relancer automatiquement ce prospect."}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Input
                    type="date"
                    className="h-8 w-40 bg-white"
                    value={relanceDate || (p.prochaine_relance ? p.prochaine_relance.slice(0, 10) : "")}
                    onChange={(e) => setRelanceDate(e.target.value)}
                    data-testid="relance-date-input"
                  />
                  <Button size="sm" variant="outline" onClick={() => relanceDate && saveRelanceDate(relanceDate)} disabled={!relanceDate} data-testid="relance-date-save">
                    Modifier la date
                  </Button>
                  {p.prochaine_relance && (
                    <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => saveRelanceDate(null)} data-testid="relance-date-cancel">
                      Annuler la relance
                    </Button>
                  )}
                </div>
              </div>
            )}

            <div>
              <h3 className="flex items-center gap-2 font-display text-sm font-semibold"><Mail size={15} /> Historique des e-mails (MODE TEST — non expédiés réellement)</h3>
              <div className="mt-2 space-y-2" data-testid="prospect-emails-list">
                {(data.emails || []).length === 0 && <p className="text-sm text-muted-foreground">Aucun e-mail. Lancez une campagne pour ce prospect.</p>}
                {(data.emails || []).map((e) => (
                  <details key={e.id} className="rounded-md border p-2.5 text-sm">
                    <summary className="flex cursor-pointer flex-wrap items-center gap-2">
                      <Badge variant={e.statut === "envoye" ? "default" : e.statut === "annule" ? "destructive" : "secondary"}>
                        {e.statut === "envoye" ? "Envoyé (test)" : e.statut === "annule" ? "Annulé" : "Planifié"}
                      </Badge>
                      <span className="font-medium">{EMAIL_TYPES[e.type]}</span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock size={12} /> {new Date(e.scheduled_at).toLocaleString("fr-FR")}</span>
                    </summary>
                    <div className="mt-2 border-t pt-2">
                      <p className="text-xs text-muted-foreground">De : {e.expediteur} · À : {e.destinataire}</p>
                      <p className="mt-1 font-medium">{e.sujet}</p>
                      <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{e.corps}</p>
                      {e.motif_annulation && <p className="mt-1 text-xs text-red-600">Motif : {e.motif_annulation}</p>}
                    </div>
                  </details>
                ))}
              </div>
            </div>

            {(data.reponses || []).length > 0 && (
              <div>
                <h3 className="flex items-center gap-2 font-display text-sm font-semibold"><MessageSquare size={15} /> Réponses reçues</h3>
                <div className="mt-2 space-y-2">
                  {data.reponses.map((r) => (
                    <div key={r.id} className="rounded-md border p-2.5 text-sm">
                      <p className="whitespace-pre-wrap">{r.texte}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{CATEGORIE_LABELS[r.categorie]} · {new Date(r.created_at).toLocaleString("fr-FR")}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="rounded-md border border-dashed p-3">
              <p className="text-sm font-medium">Simulateur de réponse (mode test)</p>
              <p className="text-xs text-muted-foreground">Collez ici la réponse du prospect : l'IA la classe, arrête les relances et crée un lead si nécessaire.</p>
              <Textarea rows={3} className="mt-2" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Ex : Bonjour, votre offre m'intéresse, pouvez-vous m'envoyer vos tarifs ?" data-testid="reply-simulator-input" />
              <Button size="sm" className="mt-2" onClick={simulate} disabled={analyzing || !reply.trim()} data-testid="reply-simulator-submit">
                {analyzing ? "Analyse IA en cours…" : "Analyser la réponse"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
